import { useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import type { User } from 'firebase/auth'
import { LogOut, Trash2, Loader2, UserRound } from 'lucide-react'
import { SettingsRow } from '../../components/SettingsCard'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useDeleteAccount } from '../../hooks/useDeleteAccount'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { signOutFromAll } from '../../lib/auth'
import { track } from '../../lib/analytics'
import { classifyError } from '../../lib/errors'
import { toastError } from '../../lib/toast'
import { beaconUserFallback } from '../../lib/commonStrings'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { PasswordSettingsRow } from './PasswordSettingsRow'
import { SettingsSection } from './SettingsSection'

export function AccountSection({ user }: { user: User }) {
  const navigate = useTransitionNavigate()
  const { deleteAccount, deleting } = useDeleteAccount()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const uids = useMemo(() => [user.uid], [user.uid])
  const displayName = useUserProfiles(uids)[user.uid]?.displayName || user.displayName || beaconUserFallback()

  async function handleDeleteAccount() {
    setConfirmOpen(false)
    const toastId = toast.loading(
      fbt('Deleting your account…', 'Loading toast while the account is being deleted').toString(),
    )
    try {
      await deleteAccount()
      track('delete_account', {})
      toast.success(
        fbt('Your account has been deleted.', 'Toast after the account is deleted').toString(),
        { id: toastId },
      )
      navigate('/', { replace: true })
    } catch (err) {
      toast.dismiss(toastId)
      toastError(
        fbt(
          "Couldn't delete your account. Please try again.",
          'Toast shown when account deletion fails',
        ).toString(),
        classifyError(err),
      )
    }
  }

  return (
    <SettingsSection title={<fbt desc="Account section label in Settings">Account</fbt>}>
      <SettingsRow
        icon={<UserRound className="size-4" />}
        label={displayName}
        description={user.email ?? undefined}
      />
      <PasswordSettingsRow user={user} />
      <SettingsRow
        icon={<LogOut className="size-4" />}
        label={fbt('Sign out', 'Row in Settings that signs the user out')}
        onClick={() => void signOutFromAll()}
      />
      <SettingsRow
        icon={deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
        label={<fbt desc="Button to permanently delete the user account">Delete account</fbt>}
        onClick={() => setConfirmOpen(true)}
        disabled={deleting}
        destructive
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        destructive
        title={<fbt desc="Title of the delete-account confirmation dialog">Delete your account?</fbt>}
        description={
          <fbt desc="Body of the delete-account confirmation dialog warning what is removed">
            This permanently deletes your profile, groups, friends, and saved interests. This
            can't be undone.
          </fbt>
        }
        confirmLabel={<fbt desc="Button to permanently delete the user account">Delete account</fbt>}
        confirmKeyword={fbt('delete', 'Keyword the user must type to confirm deleting their account').toString()}
        onConfirm={handleDeleteAccount}
      />
    </SettingsSection>
  )
}
